import { FC, useRef, useState } from "react";
import { useForm } from "@mantine/form";
import { Link } from "react-router-dom";

import {
  Button,
  DateInput,
  FormSection,
  Grid,
  TextAreaInput,
  TextInput,
  useTheme,
} from "@monolith-forensics/monolith-ui";

import { DateInputFormat, getDateFormat } from "@/utils/date-format";
import ButtonMenu from "@/components/Forms/components/ButtonMenu";
import UserSelector from "@/components/Forms/components/Selectors/UserSelector";
import validateAsync from "@/components/Forms/utils/zod/validateAsync";
import Loader from "@/components/Loader";
import CustomFieldRender from "@/components/Forms/components/CustomFieldRender";
import resolveCustomFieldValidations from "@/components/Forms/utils/resolveCustomFieldValidations";
import {
  CUSTOM_FIELD_CATEGORIES,
  CustomField,
  CustomFieldsQuery,
  isCustomFieldApplicableToType,
} from "@/Features/CustomFields";
import type {
  LabAssetCustomFieldValue,
  LabAssetIssuedToUser,
  LabAssetType,
} from "@/api/lab-assets/lab-assets.types";
import { ASSET_TYPE_IDS } from "../labAssets.constants";
import {
  CUSTOM_FIELD_KEY_PREFIX,
  EMPTY_FORM_DATA,
  LAB_ASSET_FORM_FIELDS,
  LabAssetCustomValues,
  LabAssetDirtyFields,
  LabAssetFormData,
  LabAssetPayload,
  isCustomValueEqual,
  labAssetValidationSchema,
  requiredAssetNumberSchema,
  toCustomFieldFormValues,
  toIssuedToId,
} from "./labAssetForm.utils";

// Re-exported so existing importers (CreateLabAssetForm) keep one source.
export type {
  LabAssetCustomValues,
  LabAssetDirtyFields,
  LabAssetFormData,
  LabAssetPayload,
};

interface DefaultLabAssetFormProps {
  // Scopes which asset custom fields render (a field's type_ids).
  assetType: LabAssetType;
  DefaultFormData?: LabAssetFormData;
  // The stored custom values (edit mode only).
  DefaultCustomFields?: LabAssetCustomFieldValue[] | null;
  // The stored assignee (edit mode only). Lets the Issued To selector show and
  // clear a user the active-only list no longer contains.
  issuedTo?: Pick<LabAssetIssuedToUser, "user_id" | "full_name"> | null;
  buttonProps?: { cancelText?: string; submitText?: string };
  // customValues holds every rendered custom field, trimmed, blanks as
  // undefined; hosts diff it with diffCustomFields.
  onSubmit: (
    data: LabAssetPayload,
    dirtyFields: LabAssetDirtyFields,
    customValues: LabAssetCustomValues
  ) => void | Promise<unknown>;
  onCancel: () => void;
  // Lets the host (create modal) lock its close while a submit is in
  // flight — closing mid-request would otherwise still create the row.
  onSubmittingChange?: (submitting: boolean) => void;
  // Create-mode hook: renders a left-aligned Back in the footer that returns
  // to the modal's type step. The edit flow has no step to go back to.
  onBack?: () => void;
  // Edit-mode hooks (the create flow passes none of these):
  // Fires whenever the form's dirty state flips, so the host can guard its
  // close with a discard confirmation.
  onDirtyChange?: (dirty: boolean) => void;
  // Disable Save while nothing has changed. PATCH /assets/:uuid rejects an
  // empty body with a 400, so a clean form must never submit.
  disableSubmitWhenClean?: boolean;
}

const toNullableString = (value: string | null | undefined): string | null => {
  if (typeof value !== "string") return value ?? null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
};

// Blank value per editor type, so every rendered custom input has a seeded
// value at mount (see EMPTY_FORM_DATA).
const emptyCustomValue = (field: CustomField) =>
  field.editor_type === "tagBox"
    ? []
    : field.editor_type === "textBox"
    ? ""
    : null;

// The form is uncontrolled, so every input's value must be in initialValues
// at mount. Custom field definitions load async (usually from the login
// prefetch cache), so wait for them before mounting the form. A failed fetch
// shows a retry instead of the form: with no definitions the form would let
// a required custom field be skipped.
const DefaultLabAssetForm: FC<DefaultLabAssetFormProps> = (props) => {
  const { data, isSuccess, isError, refetch } =
    CustomFieldsQuery.useGetCustomFields({
      category: CUSTOM_FIELD_CATEGORIES.ASSET,
    });

  if (isError) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span>Couldn't load the asset custom fields.</span>
        <Button
          variant="outlined"
          color="primary"
          size="xs"
          onClick={() => refetch()}
        >
          Retry
        </Button>
      </div>
    );
  }
  if (!isSuccess) return <Loader message="Loading..." />;

  const typeId = ASSET_TYPE_IDS[props.assetType];
  const customFields = data.data.filter(
    (field) =>
      field.enabled === 1 && isCustomFieldApplicableToType(field, typeId)
  );

  return <LabAssetForm {...props} customFields={customFields} />;
};

const LabAssetForm: FC<
  DefaultLabAssetFormProps & { customFields: CustomField[] }
> = ({
  assetType,
  customFields,
  DefaultFormData,
  DefaultCustomFields,
  issuedTo,
  buttonProps,
  onSubmit,
  onCancel,
  onSubmittingChange,
  onBack,
  onDirtyChange,
  disableSubmitWhenClean = false,
}) => {
  const theme = useTheme();
  const customKeys = customFields.map(
    (field) => `${CUSTOM_FIELD_KEY_PREFIX}${field.field_id}`
  );
  // Derived from the seed, not the live value: the create host passes no
  // defaults, and an edit of a NULL-numbered asset may stay blank.
  const requireAssetNumber = !!DefaultFormData?.asset_number;
  const [submitting, setSubmitting] = useState(false);
  const [dirty, setDirty] = useState(false);
  // Ref, not state: two clicks landing before the validation await resolves
  // would both read submitting === false and each POST. The ref flips
  // synchronously so the second click is rejected before any await.
  const submitLockRef = useRef(false);
  // onValuesChange runs from inside mantine's memoized setter chain, so it
  // must not depend on which render's closure fires. Refs keep the
  // last-reported dirty value and the current onDirtyChange reachable.
  const dirtyRef = useRef(false);
  const onDirtyChangeRef = useRef(onDirtyChange);
  onDirtyChangeRef.current = onDirtyChange;

  const updateDirty = (value: boolean) => {
    if (value === dirtyRef.current) return;
    dirtyRef.current = value;
    setDirty(value);
    onDirtyChangeRef.current?.(value);
  };

  const form = useForm<LabAssetFormData & LabAssetCustomValues>({
    mode: "uncontrolled",
    initialValues: {
      ...EMPTY_FORM_DATA,
      ...Object.fromEntries(
        customFields.map((field, i) => [customKeys[i], emptyCustomValue(field)])
      ),
      ...DefaultFormData,
      ...toCustomFieldFormValues(DefaultCustomFields),
    },
    // Uncontrolled mode doesn't re-render on input, so this is the only hook
    // for dirty tracking. Compares the values mantine hands us against its
    // initial values directly, not form.isDirty(): with no path, isDirty()
    // reads the internal dirty map once it has any key, so it only tracks
    // this change if mantine updated that map before firing this callback.
    // The field-wise compare doesn't depend on that ordering, and a field
    // edited back to its original reads clean again. Values are flat
    // scalars (strings, null, or the Issued To user_id), so strict equality
    // per key is a full compare and agrees with mantine's own dirty map.
    // Custom values can be tagBox arrays and clear to null/""/[]
    // interchangeably, so they compare by value with blanks equal.
    onValuesChange: (values) => {
      const initial = form.getInitialValues();
      updateDirty(
        LAB_ASSET_FORM_FIELDS.some((key) => values[key] !== initial[key]) ||
          customKeys.some(
            (key) => !isCustomValueEqual(values[key], initial[key])
          )
      );
    },
  });

  // Keeps the stored assignee selectable when the active-only user list
  // excludes them (deactivated, or an observer). UserSelector drops it when
  // the list already has the id, so the suffix only shows for the excluded
  // case. A deleted user has null names, hence the id fallback.
  const issuedToPinnedOption =
    issuedTo && DefaultFormData?.issued_to_id === issuedTo.user_id
      ? {
          value: issuedTo.user_id,
          label: `${
            issuedTo.full_name ?? `User ${issuedTo.user_id}`
          } (inactive)`,
        }
      : undefined;

  const updateSubmitting = (value: boolean) => {
    setSubmitting(value);
    onSubmittingChange?.(value);
  };

  const runSubmit = async () => {
    const formData = form.getValues();

    const normalized = {
      asset_number: toNullableString(formData.asset_number),
      name: toNullableString(formData.name),
      category: toNullableString(formData.category),
      model: toNullableString(formData.model),
      vendor: toNullableString(formData.vendor),
      serial_number: toNullableString(formData.serial_number),
      barcode: toNullableString(formData.barcode),
      purchase_date: toNullableString(formData.purchase_date),
      cost: toNullableString(formData.cost),
      location: toNullableString(formData.location),
      description: toNullableString(formData.description),
      issued_to_id: toIssuedToId(formData.issued_to_id),
    };

    // Blanks as undefined so a required rule reports "<name> is required"
    // rather than a type error; tagBox [] keeps its own min(1) message.
    const customValues: LabAssetCustomValues = Object.fromEntries(
      customKeys.map((key) => {
        const value = formData[key];
        const trimmed = typeof value === "string" ? value.trim() : value;
        return [key, trimmed === "" || trimmed === null ? undefined : trimmed];
      })
    );

    const validationResult = await validateAsync(
      (requireAssetNumber
        ? requiredAssetNumberSchema
        : labAssetValidationSchema
      ).extend(
        resolveCustomFieldValidations(customFields, ASSET_TYPE_IDS[assetType])
      )
    )({ ...normalized, ...customValues });
    if (validationResult?.hasErrors) {
      form.setErrors(validationResult.errors);
      return;
    }

    const payload: LabAssetPayload = {
      ...normalized,
      cost: normalized.cost === null ? null : Number(normalized.cost),
    };

    updateSubmitting(true);
    try {
      await onSubmit?.(
        payload,
        form.getDirty() as LabAssetDirtyFields,
        customValues
      );
    } finally {
      updateSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (submitLockRef.current) return;
    submitLockRef.current = true;

    try {
      await runSubmit();
    } finally {
      submitLockRef.current = false;
    }
  };

  // No form.reset() here: both hosts unmount the form when they close (the
  // mfui Modal drops its children on close), and the edit host's Cancel →
  // "Keep editing" must return to the user's edits intact.
  const handleCancel = () => {
    onCancel?.();
  };

  const submitDisabled = submitting || (disableSubmitWhenClean && !dirty);

  return (
    <>
      <Grid col={2}>
        <TextInput
          size="sm"
          variant="outlined"
          label="Asset Number"
          placeholder={
            // The API only generates on create; a blank edit stays NULL.
            DefaultFormData
              ? "Enter asset number"
              : "Leave blank to auto-generate"
          }
          description="Unique identifier for this asset. Leave blank on create to have one generated."
          required={requireAssetNumber}
          key={form.key("asset_number")}
          {...form.getInputProps("asset_number")}
        />
        <TextInput
          size="sm"
          variant="outlined"
          label="Name"
          placeholder="Enter asset name"
          description="Name of the asset"
          required
          key={form.key("name")}
          {...form.getInputProps("name")}
        />
        <TextInput
          size="sm"
          variant="outlined"
          label="Category"
          placeholder="Enter category"
          description="Category of the asset"
          key={form.key("category")}
          {...form.getInputProps("category")}
        />
        <TextInput
          size="sm"
          variant="outlined"
          label="Model"
          placeholder="Enter the model name"
          description="Model of the asset"
          key={form.key("model")}
          {...form.getInputProps("model")}
        />
        <TextInput
          size="sm"
          variant="outlined"
          label="Vendor"
          placeholder="Enter vendor name"
          description="Vendor or manufacturer of the asset"
          key={form.key("vendor")}
          {...form.getInputProps("vendor")}
        />
        <TextInput
          size="sm"
          variant="outlined"
          label="Serial Number"
          placeholder="Enter serial number"
          description="Serial number of the asset"
          key={form.key("serial_number")}
          {...form.getInputProps("serial_number")}
        />
        <TextInput
          size="sm"
          variant="outlined"
          label="Barcode"
          placeholder="Enter barcode"
          description="Barcode of the asset"
          key={form.key("barcode")}
          {...form.getInputProps("barcode")}
        />
        <DateInput
          size="sm"
          variant="outlined"
          label="Purchase Date"
          description="Enter the date the asset was purchased"
          clearable={true}
          arrow={true}
          enableCalendar={true}
          format={
            getDateFormat({
              isMoment: true,
              includeTime: false,
            }) as DateInputFormat
          }
          key={form.key("purchase_date")}
          {...form.getInputProps("purchase_date")}
        />
        <TextInput
          size="sm"
          variant="outlined"
          label="Cost"
          // type="text": a type="number" input sanitizes anything that isn't
          // a valid float (e.g. "12,50") to "", which would normalize to null
          // and save silently. As text, currencyRegex gates what was typed.
          type="text"
          inputProps={{ inputMode: "decimal" }}
          placeholder="Enter the cost"
          description="Cost of the asset"
          key={form.key("cost")}
          {...form.getInputProps("cost")}
        />
        <TextInput
          size="sm"
          variant="outlined"
          label="Location"
          placeholder="Enter location"
          description="Where the asset is located"
          key={form.key("location")}
          {...form.getInputProps("location")}
        />
        {/* Same mantine-uncontrolled wiring as Offices' Manager selector:
            getInputProps supplies defaultValue (the seeded user_id) and the
            uncontrolled key. onChange is overridden after the spread because
            the SelectBox clears to undefined; storing null instead keeps the
            model number | null, so mantine's dirty map and the compare in
            onValuesChange agree and a select-then-clear reads clean. */}
        <UserSelector
          label="Issued To"
          description="User this asset is currently issued to."
          query={{ includeInactive: false, includeObservers: false }}
          pinnedOption={issuedToPinnedOption}
          key={form.key("issued_to_id")}
          {...form.getInputProps("issued_to_id")}
          onChange={(value) =>
            form.setFieldValue("issued_to_id", value ?? null, {
              forceUpdate: false,
            })
          }
        />
        <TextAreaInput
          size="sm"
          variant="outlined"
          label="Description"
          placeholder="A brief description of the asset"
          description="Enter a brief description of the asset."
          colSpan={2}
          minRows={6}
          maxRows={24}
          key={form.key("description")}
          {...form.getInputProps("description")}
        />
      </Grid>
      <FormSection title="Custom Fields" style={{ marginTop: 15 }}>
        {customFields.length === 0 ? (
          <div style={{ fontSize: 12, color: "inherit", opacity: 0.8 }}>
            No{" "}
            <Link
              to="/settings/custom-fields"
              style={{
                color: theme.palette.primary.main,
                textDecoration: "underline",
                opacity: 1,
              }}
            >
              asset custom fields
            </Link>{" "}
            enabled
          </div>
        ) : (
          <Grid col={2}>
            {customFields.map((field, i) => (
              <CustomFieldRender
                key={form.key(customKeys[i])}
                field={field}
                inputProps={form.getInputProps(customKeys[i])}
                onChange={() => {}}
              />
            ))}
          </Grid>
        )}
      </FormSection>
      <ButtonMenu>
        {/* ButtonMenu is justify-content: flex-end; the auto margin pushes
            Back to the left edge while Cancel / submit stay right. */}
        {onBack && (
          <Button
            size="xs"
            variant="subtle"
            style={{ marginRight: "auto" }}
            disabled={submitting}
            onClick={onBack}
          >
            Back
          </Button>
        )}
        <Button
          size="xs"
          variant="subtle"
          disabled={submitting}
          onClick={handleCancel}
        >
          {buttonProps?.cancelText || "Cancel"}
        </Button>
        <Button
          size="xs"
          color="primary"
          variant="contained"
          loading={submitting}
          disabled={submitDisabled}
          onClick={handleSubmit}
        >
          {buttonProps?.submitText || "Submit"}
        </Button>
      </ButtonMenu>
    </>
  );
};

export default DefaultLabAssetForm;
